import { Card, Checkbox, Col, Divider, Input, Row, Space, Typography } from 'antd'

const { Text } = Typography
const { TextArea } = Input

// Renders inline **bold** markers from fixture-authored strings as <Text strong>.
export const renderBoldMarkup = text => {
  if (typeof text !== 'string') return text
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => (
    part.startsWith('**') && part.endsWith('**')
      ? <Text key={index} strong>{part.slice(2, -2)}</Text>
      : part
  ))
}

export const isMilwaukeeAsiaEnglishForm = formConfig => (
  ['milwaukee_asia', 'milwaukee_asia_english'].includes(formConfig?.form_variant)
)

export const isTtiHongKongForm = formConfig => (
  formConfig?.form_variant === 'tti_hk'
)

export const isConfiguredRegionalForm = formConfig => (
  isMilwaukeeAsiaEnglishForm(formConfig) || isTtiHongKongForm(formConfig)
)

const sectionsConfig = formConfig => formConfig?.appraisal_form?.hong_kong_sections || {}
const cultureEntries = formData => formData?.hong_kong_culture_values || {}
const developmentPlan = formData => formData?.hong_kong_development_plan || {}

export function validateMilwaukeeAsiaEnglishEmployeeSections (formData, formConfig) {
  if (!isConfiguredRegionalForm(formConfig)) return []

  const config = sectionsConfig(formConfig)
  const cultureConfig = config.culture_values || {}
  const values = cultureEntries(formData)
  const selected = (cultureConfig.values || []).filter(value => values[value.key]?.selected)
  const minimum = cultureConfig.minimum_selections ?? 2
  const maximum = cultureConfig.maximum_selections ?? 3
  const errors = []

  if (selected.length < minimum || selected.length > maximum) {
    errors.push(`${cultureConfig.title || 'Culture and Values'}: select ${minimum}-${maximum} values.`)
  }
  selected.forEach(value => {
    if (!(values[value.key]?.employee_example || '').trim()) {
      errors.push(`${value.label}: an employee example is required.`)
    }
  })

  const plan = developmentPlan(formData)
  const planLabel = (config.development_plan?.title || 'Individual Development Plan').replace(/\s*\n\s*/g, ' ').trim()
  ;(config.development_plan?.fields || []).filter(field => field.input !== false).forEach(field => {
    if (!(plan[field.key] || '').trim()) {
      errors.push(`${planLabel} → ${field.label} is required.`)
    }
  })
  return errors
}

export function validateMilwaukeeAsiaEnglishManagerSections (formData, formConfig) {
  if (!isConfiguredRegionalForm(formConfig)) return []

  const values = cultureEntries(formData)
  return ((sectionsConfig(formConfig).culture_values || {}).values || [])
    .filter(value => values[value.key]?.selected)
    .flatMap(value => (
      (values[value.key]?.manager_comment || '').trim()
        ? []
        : [`${value.label}: a manager comment is required.`]
    ))
}

export function MilwaukeeAsiaEnglishSections ({
  formData,
  formConfig,
  mode,
  onChange,
  showCulture = true,
  showDevelopmentPlan = true,
  showManagerFields = true,
  cultureSide = 'both',
  showValidationErrors = false
}) {
  if (!isConfiguredRegionalForm(formConfig)) return null

  const config = sectionsConfig(formConfig)
  const cultureConfig = config.culture_values || {}
  const values = cultureEntries(formData)
  const plan = developmentPlan(formData)
  const selectedCount = Object.values(values).filter(value => value?.selected).length
  const maximum = cultureConfig.maximum_selections ?? 3
  const employeeEditable = mode === 'employee' || mode === 'translation'
  const managerEditable = mode === 'manager' || mode === 'translation'
  const showEmployeeCultureFields = cultureSide !== 'manager'
  const showManagerCultureFields = showManagerFields && cultureSide !== 'employee'
  const cultureTitle = cultureSide === 'employee'
    ? (cultureConfig.employee_title || cultureConfig.title || 'Milwaukee Culture Expectations and Values')
    : cultureSide === 'manager'
      ? (cultureConfig.manager_title || cultureConfig.title || 'Milwaukee Culture Expectations and Values')
      : (cultureConfig.title || 'Milwaukee Culture Expectations and Values')
  const displayedCultureValues = cultureSide === 'manager'
    ? (cultureConfig.values || []).filter(value => values[value.key]?.selected)
    : (cultureConfig.values || [])

  const minimumSelections = cultureConfig.minimum_selections ?? 2
  const cultureSelectionInvalid = selectedCount < minimumSelections || selectedCount > maximum
  const selectedCultureValues = (cultureConfig.values || []).filter(value => values[value.key]?.selected)
  const cultureSectionError = showValidationErrors && (
    (employeeEditable && (
      cultureSelectionInvalid ||
      selectedCultureValues.some(value => !(values[value.key]?.employee_example || '').trim())
    )) ||
    (managerEditable && showManagerCultureFields &&
      selectedCultureValues.some(value => !(values[value.key]?.manager_comment || '').trim()))
  )

  const employeeExampleLabel = cultureConfig.employee_example_label || "Employee's examples"
  const managerCommentLabel = cultureConfig.manager_comment_label || "Manager's comments"
  const pendingManagerReviewLabel = cultureConfig.pending_manager_review_label || 'Pending manager review'

  const updateCultureValue = (key, field, value) => {
    if (!onChange) return
    onChange({
      ...formData,
      hong_kong_culture_values: {
        ...values,
        [key]: {
          ...(values[key] || {}),
          [field]: value
        }
      }
    })
  }

  const updateDevelopmentPlan = (key, value) => {
    if (!onChange) return
    onChange({
      ...formData,
      hong_kong_development_plan: {
        ...plan,
        [key]: value
      }
    })
  }

  return (
    <>
      {showCulture && cultureConfig.enabled !== false && (
        <Card className='tti-appraisal-section-card' title={<Text strong style={{ whiteSpace: 'pre-line' }}>{cultureTitle}</Text>} style={{ marginBottom: 24, ...(cultureSectionError ? { borderColor: '#ff4d4f' } : {}) }}>
          {cultureConfig.description && cultureSide !== 'manager' && (
            <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
              {cultureConfig.description}
            </Text>
          )}
          {showValidationErrors && employeeEditable && cultureSelectionInvalid && (
            <Text type='danger' style={{ display: 'block', marginBottom: 16 }}>
              {`Select ${minimumSelections}-${maximum} values.`}
            </Text>
          )}
          <Space direction='vertical' size={0} style={{ width: '100%' }}>
            {displayedCultureValues.map(value => {
              const entry = values[value.key] || {}
              return (
                <Card key={value.key} className='tti-checkbox-selection-card' size='small' type='inner'>
                  <Checkbox
                    className='tti-checkbox-selection'
                    checked={Boolean(entry.selected)}
                    disabled={!employeeEditable || (!entry.selected && selectedCount >= maximum)}
                    onChange={event => updateCultureValue(value.key, 'selected', event.target.checked)}
                  >
                    <span className='tti-checkbox-selection-label'>
                      <Text strong style={{ whiteSpace: 'pre-line' }}>{value.label}</Text>
                    </span>
                  </Checkbox>
                  {entry.selected && (
                    <Row gutter={[16, 12]} style={{ marginTop: 12 }}>
                      {showEmployeeCultureFields && (
                        <Col span={showManagerCultureFields ? 12 : 24}>
                          <Text strong style={{ display: 'block', marginBottom: 6, whiteSpace: 'pre-line' }}>{employeeExampleLabel}</Text>
                          <TextArea
                            rows={3}
                            value={entry.employee_example || ''}
                            onChange={event => updateCultureValue(value.key, 'employee_example', event.target.value)}
                            status={showValidationErrors && employeeEditable && entry.selected && !(entry.employee_example || '').trim() ? 'error' : undefined}
                            disabled={!employeeEditable}
                          />
                        </Col>
                      )}
                      {showManagerCultureFields && (
                        <Col span={showEmployeeCultureFields ? 12 : 24}>
                          <Text strong style={{ display: 'block', marginBottom: 6, whiteSpace: 'pre-line' }}>{managerCommentLabel}</Text>
                          <TextArea
                            rows={3}
                            value={entry.manager_comment || ''}
                            onChange={event => updateCultureValue(value.key, 'manager_comment', event.target.value)}
                            status={showValidationErrors && managerEditable && entry.selected && !(entry.manager_comment || '').trim() ? 'error' : undefined}
                            disabled={!managerEditable}
                            placeholder={managerEditable ? undefined : pendingManagerReviewLabel}
                          />
                        </Col>
                      )}
                    </Row>
                  )}
                </Card>
              )
            })}
          </Space>
        </Card>
      )}

      {showDevelopmentPlan && (
        <Card
          className='tti-appraisal-section-card'
          title={<Text strong style={{ whiteSpace: 'pre-line' }}>{config.development_plan?.title || 'Individual Development Plan'}</Text>}
          style={{ marginBottom: 24 }}
        >
          {config.development_plan?.description && (
            <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
              {config.development_plan.description}
            </Text>
          )}
          <Row gutter={[16, 16]}>
            {(config.development_plan?.fields || []).map(field => field.input === false
              ? (
                <Col span={24} key={field.key}>
                  <Divider style={{ margin: '0 0 16px' }} />
                  <Text strong style={{ display: 'block', whiteSpace: 'pre-line' }}>{field.label}</Text>
                  {field.note && (
                    <Text type='secondary' style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-line' }}>{field.note}</Text>
                  )}
                </Col>
                )
              : (
              <Col span={12} key={field.key}>
                <Text strong style={{ display: 'block', marginBottom: 6, whiteSpace: 'pre-line' }}>{field.label}</Text>
                {field.note && (
                  <Text type='secondary' style={{ display: 'block', marginBottom: 6, whiteSpace: 'pre-line' }}>{field.note}</Text>
                )}
                <TextArea
                  rows={4}
                  value={plan[field.key] || ''}
                  onChange={event => updateDevelopmentPlan(field.key, event.target.value)}
                  disabled={!employeeEditable}
                  placeholder={employeeEditable ? undefined : 'Not provided'}
                />
              </Col>
                ))}
          </Row>
        </Card>
      )}
    </>
  )
}
